import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, LineChart, Line,
} from 'recharts';
import { Users, Wallet, AlertTriangle, TrendingUp, ArrowUpRight } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import { paidByInvoice, invoiceOutstanding, groupBy } from '../../lib/fees';
import { formatRM } from '../../lib/format';
import PageHeader from '../../components/PageHeader';
import AnimatedMoney from '../../components/AnimatedMoney';
import { CardSkeleton } from '../../components/Skeletons';
import { StaggerList, StaggerItem } from '../../components/Motion';
import { useRenderProfiler, measureAsync } from '../../lib/perfMonitor';

function Kpi({ icon: Icon, label, children, tone = 'brand', delta }) {
  const tones = {
    brand: 'bg-brand-light text-brand',
    green: 'bg-emerald2-light text-emerald2',
    red: 'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400',
    amber: 'bg-amber2-light text-amber-600 dark:text-amber-400',
  };
  return (
    <StaggerItem className="rounded-2xl border border-border bg-card p-4 sm:p-5 hover:-translate-y-0.5 hover:shadow-md transition-all">
      <div className="flex items-center justify-between gap-2">
        <span className={`h-9 w-9 sm:h-10 sm:w-10 rounded-xl flex items-center justify-center shrink-0 ${tones[tone]}`}><Icon className="h-5 w-5" /></span>
        {delta && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald2 truncate min-w-0"><ArrowUpRight className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{delta}</span></span>}
      </div>
      <p className="mt-3 sm:mt-4 text-sm font-medium text-muted-foreground">{label}</p>
      <div className="mt-1 text-xl sm:text-3xl font-black text-foreground tracking-tight">{children}</div>
    </StaggerItem>
  );
}

const DASHBOARD_CACHE_KEY = 'mtsr_dashboard_summary';

function getCachedDashboard() {
  try {
    const raw = localStorage.getItem(DASHBOARD_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function Dashboard() {
  useRenderProfiler('BendaharaDashboard');

  const { t } = useTranslation();
  const { profile } = useAuth();
  const cached = getCachedDashboard();
  const [loading, setLoading] = useState(!cached);
  const [data, setData] = useState(cached);

  useEffect(() => {
    (async () => {
      await measureAsync('Bendahara Dashboard Loading & Aggregation', async () => {
        const [{ data: students }, { data: classes }, { data: invoices }, { data: payments }] = await Promise.all([
          supabase.from('students').select('id, class_id, aktif'),
          supabase.from('classes').select('id, nama'),
          supabase.from('invoices').select('id, student_id, jumlah_bayar_rm, status'),
          supabase.from('payments').select('id, invoice_id, jumlah_rm, tanggal, voided'),
        ]);
        const paidMap = paidByInvoice(payments || []);
        const classMap = Object.fromEntries((classes || []).map((c) => [c.id, c.nama]));
        const studentClass = Object.fromEntries((students || []).map((s) => [s.id, s.class_id]));

        let totalOutstanding = 0, totalBilled = 0, totalPaid = 0, bilBelum = 0;
        const perClass = {};
        for (const inv of invoices || []) {
          const os = invoiceOutstanding(inv, paidMap);
          totalOutstanding += os;
          totalBilled += Number(inv.jumlah_bayar_rm || 0);
          totalPaid += paidMap[inv.id] || 0;
          if (os > 0) bilBelum += 1;
          const cid = studentClass[inv.student_id];
          const cname = classMap[cid] || 'Lain-lain';
          perClass[cname] = (perClass[cname] || 0) + os;
        }
        const chartData = Object.entries(perClass).map(([nama, tunggakan]) => ({ nama, tunggakan }))
          .sort((a, b) => b.tunggakan - a.tunggakan);
        const topClass = chartData[0];

        // kutipan bulan ini + sparkline 14 hari
        const now = new Date();
        const monthKey = `${now.getFullYear()}-${now.getMonth()}`;
        let kutipanBulan = 0;
        const dayMap = {};
        for (const p of payments || []) {
          if (p.voided) continue;
          const d = new Date(p.tanggal);
          if (`${d.getFullYear()}-${d.getMonth()}` === monthKey) kutipanBulan += Number(p.jumlah_rm || 0);
          const key = d.toISOString().slice(0, 10);
          dayMap[key] = (dayMap[key] || 0) + Number(p.jumlah_rm || 0);
        }
        const spark = [];
        for (let i = 13; i >= 0; i--) {
          const d = new Date(now); d.setDate(d.getDate() - i);
          const key = d.toISOString().slice(0, 10);
          spark.push({ hari: key.slice(5), nilai: dayMap[key] || 0 });
        }
        const kadar = totalBilled > 0 ? Math.round((totalPaid / totalBilled) * 100) : 0;

        const freshData = {
          pelajar: (students || []).filter((s) => s.aktif !== false).length,
          totalOutstanding, kutipanBulan, bilBelum, chartData, topClass, spark, kadar,
        };
        setData(freshData);
        try { localStorage.setItem(DASHBOARD_CACHE_KEY, JSON.stringify(freshData)); } catch {}
      }, { page: 'BendaharaDashboard' });
      setLoading(false);
    })();
  }, []);

  return (
    <div>
      <PageHeader
        title={`${t('dashboard')}`}
        subtitle={profile ? `Selamat datang, ${profile.nama}` : ''}
        breadcrumbs={[{ label: t('utama') }, { label: t('dashboard') }]}
      />

      {loading ? <CardSkeleton /> : (
        <>
          <StaggerList className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <Kpi icon={AlertTriangle} label={t('jumlahTunggakan')} tone="red">
              <AnimatedMoney value={data.totalOutstanding} testid="kpi-tunggakan" className="text-red-600 dark:text-red-400" />
            </Kpi>
            <Kpi icon={Wallet} label="Kutipan Bulan Ini" tone="green" delta={`${data.kadar}% terkumpul`}>
              <AnimatedMoney value={data.kutipanBulan} testid="kpi-kutipan" className="text-emerald2" />
            </Kpi>
            <Kpi icon={Users} label="Jumlah Pelajar" tone="brand">
              <span data-testid="kpi-pelajar">{data.pelajar}</span>
            </Kpi>
            <Kpi icon={TrendingUp} label="Bil Belum Selesai" tone="amber">
              <span data-testid="kpi-bil">{data.bilBelum}</span>
            </Kpi>
          </StaggerList>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6">
              <h2 className="text-lg font-bold text-foreground">
                {data.topClass && data.topClass.tunggakan > 0
                  ? `${data.topClass.nama} tunggakan tertinggi: ${formatRM(data.topClass.tunggakan)}`
                  : 'Tiada tunggakan buat masa ini 🎉'}
              </h2>
              <p className="text-sm text-muted-foreground mb-4">{t('tunggakanMengikutKelas')}</p>
              {data.chartData.length === 0 ? (
                <p className="py-12 text-center text-muted-foreground">{t('tiadaData')}</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis dataKey="nama" tick={{ fontSize: 14, fontWeight: 600, fill: 'hsl(var(--foreground))' }} />
                    <YAxis tick={{ fontSize: 13, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => `RM${v}`} width={70} />
                    <Tooltip formatter={(v) => formatRM(v)} cursor={{ fill: 'hsl(var(--muted))' }}
                             contentStyle={{ borderRadius: 12, backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', color: 'hsl(var(--card-foreground))', fontSize: 14 }} />
                    <Bar dataKey="tunggakan" radius={[8, 8, 0, 0]} maxBarSize={72}>
                      {data.chartData.map((_, i) => <Cell key={i} fill={i === 0 ? '#DC2626' : '#F59E0B'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="text-lg font-bold text-foreground">{t('aliranKutipan')}</h2>
              <p className="text-sm text-muted-foreground mb-4">14 hari terakhir</p>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data.spark} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <XAxis dataKey="hari" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} interval={3} />
                  <Tooltip formatter={(v) => formatRM(v)} contentStyle={{ borderRadius: 12, backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', color: 'hsl(var(--card-foreground))', fontSize: 13 }} />
                  <Line type="monotone" dataKey="nilai" stroke="#10B981" strokeWidth={3} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
